import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useConsole, useRead } from '../context';
import { Empty, Failure, Loading } from '../bits';
import { Panel, Segmented } from '../kit';
import { Feed } from './activity/Feed';
import { useT } from '../../messages';

/**
 * **Activity** — what has been done to this business, as sentences (console
 * redesign, phase B6; the mockup's Activity panel).
 *
 * "Jose turned on gift cards and reports", newest first, with the minute
 * beside it and the row as written one click away. The trail holds whoever
 * wrote under this business's id — our operators (`operator:…`), the
 * business's own staff (`staff:…`), the CLI and the importer — and the **who**
 * filter splits the first two apart, asked of the server so a page is a full
 * page of what was asked for.
 *
 * **Changes, unless looks are asked for.** Opening one business page writes a
 * dozen audited reads, and a feed of "opened the business page" buries the
 * line that says a price changed; so the server leaves them out
 * (`changes_only`) until the operator ticks "Include when someone only
 * looked". They are still recorded, and the Log shows them the same way.
 *
 * What comes back is the server's *summary* of each row, never its detail: a
 * price diff, a module list, a status — no guest, no name, no address. That is
 * why a sentence here can be shorter than the same row's on the Log ("changed
 * an item" rather than "changed “Swedish Massage”"): the name was dropped on
 * the way out, deliberately, and `sentences.ts` says what is left.
 *
 * **Paged by id, not by offset.** The trail is append-only and the desk keeps
 * writing while an operator reads; a cursor on the last `audit_id` seen does
 * not move when rows land above it, where an offset would show the same row
 * twice at every boundary. "Older" pushes the cursor, "Newer" pops it, and the
 * first page is the newest. There is no total: counting an append-only table
 * is the unbounded read the server refuses to make.
 */
type Who = 'everyone' | 'business' | 'operators';

export function ActivityTab({ tenantId }: { tenantId: string }): ReactNode {
  const api = useApi();
  const t = useT();
  const { config } = useConsole();

  const [who, setWho] = useState<Who>('everyone');
  const [looks, setLooks] = useState(false);
  /** The cursors walked so far; the top is the page on screen, empty is the newest. */
  const [cursors, setCursors] = useState<readonly string[]>([]);
  const before = cursors[cursors.length - 1];

  const page = useRead(
    () =>
      api.activity(tenantId, {
        before,
        limit: config.pageSize,
        who: who === 'everyone' ? undefined : who,
        changesOnly: !looks,
      }),
    [tenantId, before, config.pageSize, who, looks],
  );

  const older = page.value?.next_before ?? null;
  const filtered = who !== 'everyone' || !looks;

  return (
    <Panel
      title={t('activity.title')}
      actions={
        <>
          <Segmented<Who>
            label={t('activity.who')}
            options={[
              { id: 'everyone', label: t('activity.who.everyone') },
              { id: 'business', label: t('activity.who.business') },
              { id: 'operators', label: t('activity.who.operators') },
            ]}
            value={who}
            onChange={(next) => {
              setCursors([]);
              setWho(next);
            }}
          />
          <label className="inline-field">
            <input
              type="checkbox"
              checked={looks}
              onChange={(e) => {
                setCursors([]);
                setLooks(e.target.checked);
              }}
            />
            <span>{t('activity.looks')}</span>
          </label>
        </>
      }
    >
      {page.failure !== null ? <Failure failure={page.failure} /> : null}
      {page.value === null ? (
        page.loading ? (
          <Loading what={t('activity.loading')} />
        ) : null
      ) : page.value.entries.length === 0 ? (
        <Empty>
          {filtered && cursors.length === 0 ? t('activity.emptyFiltered') : t('activity.empty')}
        </Empty>
      ) : (
        <Feed
          entries={page.value.entries.map((e) => ({
            id: e.audit_id,
            at: e.at,
            actor: e.actor,
            action: e.action,
            target: e.target,
            detail: e.summary,
          }))}
        />
      )}
      <div className="afeed__pager">
        <button
          type="button"
          className="button"
          disabled={page.loading || cursors.length === 0}
          onClick={() => setCursors((c) => c.slice(0, -1))}
        >
          {t('activity.newer')}
        </button>
        <button
          type="button"
          className="button"
          disabled={page.loading || older === null}
          onClick={() => {
            if (older !== null) setCursors((c) => [...c, older]);
          }}
        >
          {t('activity.older')}
        </button>
      </div>
    </Panel>
  );
}
